import React, { useState } from 'react';
import { submitEarlyAccess, type EarlyAccessPayload } from '../../services/marketingChatService';

interface MarketingEarlyAccessProps {
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  expanded: boolean;
  onToggle: () => void;
}

const MarketingEarlyAccess: React.FC<MarketingEarlyAccessProps> = ({
  open,
  onClose,
  collapsed,
  expanded,
  onToggle,
}) => {
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    agencyName: '',
    isCustomer: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const payload: EarlyAccessPayload = {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        agencyName: form.agencyName.trim(),
        isCurrentCustomer: form.isCustomer === 'yes',
      };
      const result = await submitEarlyAccess(payload);
      if (result.duplicate) {
        setDuplicate(true);
      }
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const panelClass = [
    'mkt-right-panel',
    open ? 'mkt-right-panel--open' : '',
    collapsed ? 'mkt-right-panel--collapsed' : '',
    expanded ? 'mkt-right-panel--expanded' : '',
  ].filter(Boolean).join(' ');

  return (
    <aside className={panelClass}>
      <div className="mkt-right-panel__top">
        <button className="mkt-right-panel__close" onClick={onClose} aria-label="Close panel">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        <button className="mkt-right-panel__toggle" onClick={onToggle} aria-label={collapsed ? 'Expand panel' : 'Collapse panel'}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d={collapsed ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
          </svg>
        </button>
      </div>

      {!collapsed && (
        <div className="mkt-ea-card">
          <h3>Request Early Access</h3>
          <p className="mkt-ea-card__desc">
            Join a select group of agencies getting first access to the future of insurance operations.
          </p>

          {submitted ? (
            <div className="mkt-ea-card__success">
              {duplicate
                ? "You're already on the list. We'll be in touch with details about beta access."
                : "You're on the waitlist! We'll be in touch with details about beta access."}
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mkt-ea-card__group">
                <label htmlFor="ea-name">Full Name *</label>
                <input
                  id="ea-name"
                  type="text"
                  name="fullName"
                  placeholder="Your name"
                  value={form.fullName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mkt-ea-card__group">
                <label htmlFor="ea-email">Email Address *</label>
                <input
                  id="ea-email"
                  type="email"
                  name="email"
                  placeholder="you@agency.com"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mkt-ea-card__group">
                <label htmlFor="ea-agency">Agency Name</label>
                <input
                  id="ea-agency"
                  type="text"
                  name="agencyName"
                  placeholder="Your agency"
                  value={form.agencyName}
                  onChange={handleChange}
                />
              </div>

              <fieldset className="mkt-ea-card__group" style={{ border: 'none', padding: 0, margin: 0, marginBottom: '14px' }}>
                <legend style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500, marginBottom: '6px' }}>
                  Are you a current Momentum AMP customer?
                </legend>
                <div className="mkt-ea-card__radios">
                  <label className="mkt-ea-card__radio">
                    <input
                      type="radio"
                      name="isCustomer"
                      value="yes"
                      checked={form.isCustomer === 'yes'}
                      onChange={handleChange}
                    /> Yes
                  </label>
                  <label className="mkt-ea-card__radio">
                    <input
                      type="radio"
                      name="isCustomer"
                      value="no"
                      checked={form.isCustomer === 'no'}
                      onChange={handleChange}
                    /> No
                  </label>
                </div>
              </fieldset>

              {error && <div className="mkt-ea-card__error">{error}</div>}
              <button className="mkt-ea-card__cta" type="submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'GET ON THE LIST'}
              </button>
            </form>
          )}
        </div>
      )}
    </aside>
  );
};

export default MarketingEarlyAccess;
